import { CustomDiceChromecastMessage, DICE_MESSAGING_CAST_CHANNEL } from '@dicetechnology/dice-doris';
import { hasKeyOfType } from '@dicetechnology/doris-core';
import type { Logger, Player } from '@dicetechnology/doris-core';
import autoBind from 'auto-bind';
import type { cast as castNs } from 'chromecast-caf-receiver';

import type { PlaylistManager } from '../PlaylistManager';

import type { CustomMessageListenerInit, GetIsAutoAdvanceEnabledMessage, MessageEvent } from './types';

declare const cast: typeof castNs;

export class CustomMessageListener {
    private _isConfigured: boolean;
    private _logger?: Logger;
    private _playlistManager: PlaylistManager;

    constructor(init: CustomMessageListenerInit) {
        autoBind(this);

        const { playlistManager } = init;

        this._isConfigured = false;
        this._playlistManager = playlistManager;
    }

    public configureCustomMessageListener(player: Player): void {
        if (this._isConfigured) {
            return;
        }

        const { log } = player.getServices();
        this._logger = log.createLogger('VesperCastCustomMessageListener');

        const context = cast.framework.CastReceiverContext.getInstance();
        context.addCustomMessageListener(DICE_MESSAGING_CAST_CHANNEL, this.handleCustomMessage);

        this._isConfigured = true;
    }

    private handleCustomMessage(event: MessageEvent): void {
        if (!event?.data?.type) {
            return;
        }
        switch (event.data.type) {
            case CustomDiceChromecastMessage.SET_AUTO_ADVANCE_ENABLED: {
                let nextState = !this._playlistManager.getIsAutoAdvanceEnabled();
                if (hasKeyOfType(event.data, 'isAutoAdvanceEnabled', 'boolean')) {
                    nextState = event.data.isAutoAdvanceEnabled;
                }
                this._logger?.debug(`Setting auto-advance to ${nextState}`);
                this._playlistManager.setIsAutoAdvanceEnabled(nextState);
                this.sendAutoAdvanceEnabledMessage();
                break;
            }
            case CustomDiceChromecastMessage.GET_AUTO_ADVANCE_ENABLED: {
                this.sendAutoAdvanceEnabledMessage();
                break;
            }
            default: {
                this._logger?.warn('Unknown custom message type!', event);
            }
        }
    }

    private sendAutoAdvanceEnabledMessage(): void {
        const isAutoAdvanceEnabled = this._playlistManager.getIsAutoAdvanceEnabled();
        const context = cast.framework.CastReceiverContext.getInstance();
        const message: GetIsAutoAdvanceEnabledMessage = {
            isAutoAdvanceEnabled,
            type: CustomDiceChromecastMessage.AUTO_ADVANCE_ENABLED
        };

        this._logger?.debug(`Sending auto-advance enabled state: ${isAutoAdvanceEnabled}`);
        context.sendCustomMessage(DICE_MESSAGING_CAST_CHANNEL, undefined, message);
    }
}
